// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import { applyHtmlLang, getSavedLang } from "@/i18n";

// pages
import Index from "../pages/Index.vue";
import Contact from "@/pages/Contact.vue";
import TermsAndConditions from "@/pages/TermsAndConditions.vue";


import Login from "@/pages/Auth/Login.vue";
import ForgotPassword from "@/pages/Auth/ForgotPassword.vue";
import VerifyAccount from "@/pages/Auth/VerifyAccount.vue";
import RegisterClient from "@/pages/Auth/RegisterClient.vue";
import RegiserConsultant from "@/pages/Auth/RegiserConsultant.vue";
import RegisterOffice from "@/pages/Auth/RegisterOffice.vue";

import Test from "@/pages/Test.vue";
import Dashboard from "@/pages/Auth/Dashboard.vue";
import UserProfile from "@/pages/Auth/UserProfile.vue";

import CreateConsulting from "@/pages/Auth/CreateConsulting.vue";
import MyConsultings from "@/pages/Auth/MyConsultings.vue";
import ConsultantDashboard from "@/pages/Auth/ConsultantDashboard.vue";
import OfficeManagement from "@/pages/Auth/OfficeManagement..vue";
import EditConsulting from "@/pages/Auth/EditConsulting.vue";
import ShowConsulting from "@/pages/Auth/ShowConsulting.vue";
import AllConsultings from "@/pages/Auth/AllConsultings.vue";
import ConsultationChat from "@/pages/Auth/ConsultationChat.vue";
import ConsultationNegotiation from "@/pages/Auth/ConsultationNegotiation.vue";
import NotificationsPage from "@/pages/Auth/NotificationsPage.vue";
import MyTickets from "@/pages/Auth/tickets/MyTickets.vue";
import CreateTicket from "@/pages/Auth/tickets/CreateTicket.vue";
import TicketDetail from "@/pages/Auth/tickets/TicketDetail.vue";
import ComingSessionConsultations from "@/pages/Auth/consultations/ComingSessionConsultations.vue";
import ComingChatConsultations from "@/pages/Auth/consultations/ComingChatConsultations.vue";
import MyInvoices from "@/pages/Auth/MyInvoices.vue";
import AccountSettingsClient from "@/pages/Auth/AccountSettingsClient.vue";
import AccountSettingsConsulting from "@/pages/Auth/AccountSettingsConsulting.vue";
import AccountSettingsOffice from "@/pages/Auth/AccountSettingsOffice.vue";
import PrivacyPolicy from "@/pages/PrivacyPolicy.vue";
import NotFound from "@/pages/NotFound.vue";
import { hasAuthCookie } from "@/utils/authSession";
import {
  hasPendingVerification,
  isVerifyTokenExpired,
} from "@/utils/pendingVerification";

const PUBLIC_AUTH_ROUTE_NAMES = new Set([
  "Login",
  "Forgot",
  "VerifyAccount",
  "RegisterClient",
  "RegiserConsultant",
  "RegisterOffice",
]);



/* =========================
   Routes
   ========================= */
const routes = [
  // Public
  { path: "/", name: "index", component: Index },
  { path: "/contact", name: "contact", component: Contact },
 { path: "/terms-and-conditions", name: "TermsAndConditions", component: TermsAndConditions },
  {
    path: "/service-details/:id",
    name: "service-details",
    component: () => import("@/pages/ServiceDetails.vue"),
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: () => import("@/pages/PrivacyPolicy.vue"),
  },

  // Guest-only
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: { guestOnly: true },
  },
  {
    path: "/forgot",
    name: "Forgot",
    component: ForgotPassword,
    meta: { guestOnly: true },
  },
  {
    path: "/verify-account",
    name: "VerifyAccount",
    component: VerifyAccount,
    meta: { requiresVerification: true },
  },
  {
    path: "/registerClient",
    name: "RegisterClient",
    component: RegisterClient,
    props: { initialType: "client", initialStep: 0 },
    meta: { guestOnly: true },
  },
  {
    path: "/regiserConsultant",
    name: "RegiserConsultant",
    component: RegiserConsultant,
    props: { initialType: "consultant", initialStep: 0 },
    meta: { guestOnly: true },
  },
  {
    path: "/registerOffice",
    name: "RegisterOffice",
    component: RegisterOffice,
    props: { initialType: "consultant", initialStep: 0 },
    meta: { guestOnly: true },
  },

  // Protected
  {
    path: "/test",
    name: "Test",
    component: Test,
    meta: { requiresAuth: true },
  },
  {
    path: "/account/profile",
    name: "UserProfile",
    component: UserProfile,
    meta: { requiresAuth: true },
  },
  {
    path: "/account/settings/client",
    name: "ClientSettings",
    component: AccountSettingsClient,
    props: { userType: "client" },
    meta: { requiresAuth: true, roles: ["client"] },
  },
  {
    path: "/account/settings/consultant",
    name: "ConsultantSettings",
    component: AccountSettingsConsulting,
    props: { userType: "consultant" },
    meta: { requiresAuth: true, roles: ["consultant"] },
  },
  {
    path: "/account/settings/office",
    name: "OfficeSettings",
    component: AccountSettingsOffice,
    props: { userType: "office" },
    meta: { requiresAuth: true, roles: ["office"] },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/create",
    name: "consultations.create",
    component: CreateConsulting,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/my",
    name: "consultations.my",
    component: MyConsultings,
    meta: { requiresAuth: true },
  },
  {
    path: "/my-consultations/coming/session",
    name: "consultations.coming.session",
    component: ComingSessionConsultations,
    meta: { requiresAuth: true },
  },
  {
    path: "/my-consultations/coming/chat",
    name: "consultations.coming.chat",
    component: ComingChatConsultations,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/coming-session",
    redirect: "/my-consultations/coming/session",
  },
  {
    path: "/consultations/coming-chat",
    redirect: "/my-consultations/coming/chat",
  },
  {
    path: "/consultations/dashboard",
    name: "consultations.dashboard",
    component: ConsultantDashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/offices",
    name: "offices.index",
    component: OfficeManagement,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/:id/edit",
    name: "consultations.edit",
    component: EditConsulting,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/:id",
    name: "consultations.show",
    component: ShowConsulting,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/all",
    name: "consultations.show.all",
    component: AllConsultings,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/:id/chat",
    name: "ConsultationChat",
    component: ConsultationChat,
    meta: { requiresAuth: true },
  },
  {
    path: "/consultations/:id/negotiations",
    name: "ConsultationNegotiation",
    component: ConsultationNegotiation,
    meta: { requiresAuth: true },
  },
  {
    path: "/invoices/my",
    name: "MyInvoices",
    component: MyInvoices,
    meta: { requiresAuth: true },
  },
  {
    path: "/notifications",
    name: "notifications.index",
    component: NotificationsPage,
    meta: { requiresAuth: true },
  },
  {
    path: "/tickets/my",
    name: "tickets.my",
    component: MyTickets,
    meta: { requiresAuth: true },
  },
  {
    path: "/tickets/create",
    name: "tickets.create",
    component: CreateTicket,
    meta: { requiresAuth: true },
  },
  {
    path: "/tickets/:ticketNumber",
    name: "tickets.show",
    component: TicketDetail,
    meta: { requiresAuth: true },
  },

  // Legacy /admin URLs from the template — keep working without exposing an admin portal
  { path: "/admin/account/profile", redirect: "/account/profile" },
  { path: "/admin/account/settings", redirect: "/account/profile" },
  {
    path: "/admin/client/account/settings",
    redirect: "/account/settings/client",
  },
  {
    path: "/admin/consultant/account/settings",
    redirect: "/account/settings/consultant",
  },
  {
    path: "/admin/office/account/settings",
    redirect: "/account/settings/office",
  },
  { path: "/admin/settings", redirect: "/dashboard" },
  { path: "/admin/offices", redirect: "/offices" },

  // Fallback
    {
    path: "/404",
    name: "NotFound",
    component: NotFound,
  },
  // Fallback
  { path: "/:pathMatch(.*)*", name: "NotFoundCatchAll", component: NotFound },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),

  routes,
});

/* =========================
   Global Auth Guard
   ========================= */
router.beforeEach((to) => {
  const logged = hasAuthCookie();
  const pendingVerification = hasPendingVerification();
  const verifyExpired = isVerifyTokenExpired();

  if (to.name === "VerifyAccount" && verifyExpired) {
    return { name: "Login", query: { redirect: to.fullPath } };
  }

  if (to.matched.some((r) => r.meta?.requiresVerification)) {
    if (!pendingVerification) {
      return logged
        ? { name: "UserProfile" }
        : { name: "Login", query: { redirect: to.fullPath } };
    }
  }

  if (pendingVerification && !PUBLIC_AUTH_ROUTE_NAMES.has(to.name)) {
    return { name: "VerifyAccount" };
  }

  // protected routes
  if (to.matched.some((r) => r.meta?.requiresAuth)) {
    if (!logged) {
      if (pendingVerification) {
        return { name: "VerifyAccount" };
      }
      return { name: "Login", query: { redirect: to.fullPath } };
    }
  }

  // guest-only routes
  if (to.matched.some((r) => r.meta?.guestOnly)) {
    if (logged) {
      return { name: "UserProfile" };
    }
    if (pendingVerification && to.name !== "VerifyAccount") {
      return { name: "VerifyAccount" };
    }
  }

  return true;
});

// ✅ keep html lang/dir stable after every navigation
router.afterEach(() => {
  applyHtmlLang(getSavedLang());
});

export default router;
